<template>
  <div>
    <v-client-table :class="{'hide-filters': hideFilters}" :data="data" :columns="columnList" :options="tableOptions" ref="vTable">
      <div class="after-filter-wrapper" slot="afterFilter">
        <div class="d-flex justify-content-end align-items-center w-100">
          <button v-if="showSelect" class="search-button" @click="editAction()" type="button">
            {{editButtonText}}
          </button>
          <div v-if="showDownload" style="height: 45px;">
            <img src="../../assets/images/icons/excel-export-H.svg" class="dwn-button" @click="startDownload"/>
          </div>
          <el-select
              v-if="!hideFiltersList"
              v-model="visibleColumns"
              collapse-tags
              multiple
          >
            <el-option v-for="column in selectableColumnList" :label="parseMetricName(column)" :key="parseMetricName(column)"
                       :value="column">
            </el-option>
          </el-select>
        </div>
      </div>
      <div slot="c_mi_number" slot-scope="props">
        <a v-if="props.row.c_mi_number !== '-'" :href="`/study/${props.row.id}/`" target="_blank"><span>{{ props.row.c_mi_number }}</span></a>
        <span v-else>{{ props.row.c_mi_number }}</span>
      </div>
      <div
          v-for="column in multivalueColumns"
          :slot="column"
          :key="`${column}-${props.row.id}`"
          slot-scope="props"
      >
        <div
          class="column-tag-wrapper"
          v-if="Array.isArray(props.row[column])"
        >
          <span
              v-for="value in toggledFields[`${column}-${props.row.id}`] ? props.row[column] : props.row[column].slice(0, 2)"
              :key="`${column}-${props.row.id}-${value}`"
          >
            {{ value }}
          </span>
          <span
              v-if="!props.row[column].length"
              :key="`${column}-${props.row.id}-empty`"
          >
            -
          </span>
        </div>
        <span v-else>
          {{props.row[column]}}
        </span>
        <span
            class="field-toggle"
            v-if="Array.isArray(props.row[column]) && props.row[column].length > 2"
            @click.stop="toggleField(`${column}-${props.row.id}`)"
        >
          <i>{{ toggledFields[`${column}-${props.row.id}`] ? 'less' : `(${props.row[column].length - 2}) more` }}</i>
        </span>
      </div>
      <div slot="selection" slot-scope="props">
        <div class="checkbox-container">
          <Checkbox
              :key="`checkbox-${props.row.id}`"
              :value="selectedRows[props.row.id]"
              @change="(val) => changeSelectedCount(props.row.id, val)">
          </Checkbox>
        </div>
      </div>
      <div slot="metric_name" slot-scope="props">
        <span>{{ props.row.metric_name }}{{ props.row.is_binary_scale ? '*' : ''}}</span>
      </div>
      <div slot="links" slot-scope="props" class="d-flex flex-column">
        <router-link :to="`/study/${props.row.id}/structure/`" target="_blank">
          files
        </router-link>
        <router-link :to="`/study/${props.row.id}/`" target="_blank">
          details
        </router-link>
        <router-link :to="{ name: 'rdf_upload_id', params: { id: props.row.id }}" target="_blank">
          statistics
        </router-link>
      </div>
      <div slot="file_name" slot-scope="props" class="file-data d-flex justify-content-center align-items-center"
           @click="startDownload(props.row)" style="cursor: pointer;">
        <span style="text-align: center; width: 100%">{{ props.row.file_name }}</span>
        <font-awesome-icon class="file-icon ml-2"
                           :icon="getFileIcon(props.row.file_name)"></font-awesome-icon>
      </div>
    </v-client-table>
  </div>
</template>

<script>
import Checkbox from './Checkbox'
export default {
  name: 'Table',
  components: { Checkbox },
  props: {
    data: Array,
    columns: Array,
    options: Object,
    additionalColumns: {
      type: Array,
      default: () => []
    },
    isSelectionActive: Boolean,
    showChildRows: Boolean,
    showDownload: Boolean,
    showSelect: Boolean,
    hideFilters: Boolean,
    hideFiltersList: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      visibleColumns: [],
      toggledFields: {},
      selectedRows: {},
      selectedRowsCount: 0,
      multivalueColumns: [
        'client',
        'category',
        'product_type',
        'business_unit',
        'region',
        'country',
        'design_method',
        'research_type',
        'methodology',
        'cmi_tool_type',
        'evaluation'
      ],
      columnsDrawerToggled: false
    }
  },
  computed: {
    tableOptions () {
      return {
        ...this.options,
        visibleColumns: this.allVisibleColumns,
        resizableColumns: true,
        columnsDropdown: false,
        sortable: [...this.columns, ...this.additionalColumns],
        texts: {
          filterPlaceholder: 'Search query within the label displayed'
        },
        sortIcon: {
          is: 'fa-sort',
          base: 'font-awesome-icon fas',
          up: 'fa-sort-up',
          down: 'fa-sort-down'
        },
        cellClasses: this.cellClassOptions
      }
    },
    allVisibleColumns () {
      let allColumns = this.visibleColumns
      if (this.isSelectionActive) {
        allColumns = ['selection', ...allColumns]
      }
      return allColumns
    },
    cellClassOptions () {
      const classes = {}
      this.multivalueColumns.forEach(column => {
        classes[column] = [{ class: 'multivalue-highlight', condition: row => row[column].length > 1 }]
      })
      return classes
    },
    selectableColumnList () {
      return [...this.columns, ...this.additionalColumns]
    },
    columnList () {
      let allColumns = [...this.columns, ...this.additionalColumns]
      if (this.isSelectionActive) {
        allColumns = ['selection', ...allColumns]
      }
      return allColumns
    },
    editButtonText () {
      return this.isSelectionActive
        ? this.selectedRowsCount
          ? `Edit ${this.selectedRowsCount} stud${this.selectedRowsCount > 1 ? 'ies' : 'y'}`
          : 'Cancel'
        : 'Edit'
    }
  },
  methods: {
    parseMetricName (value) {
      switch (value) {
        case 'c_mi_number':
          return 'CMI #'
        case 'ref_number':
          return 'Developer #'
        case 'code':
          return '6 Digit Code (SAP)'
        default:
          const data = value.replace(/_/g, ' ')
          return data.charAt(0).toUpperCase() + data.substring(1)
      }
    },
    startDownload (props) {
      this.$emit('download', props)
    },
    setQuery (val) {
      this.$refs.vTable.setFilter(val)
    },
    toggleField (tag) {
      this.$set(this.toggledFields, tag, !this.toggledFields[tag])
    },
    changeSelectedCount (rowId, boolVal) {
      this.$set(this.selectedRows, rowId, boolVal)
      this.selectedRowsCount = Object.values(this.selectedRows).filter(b => b).length
    },
    editAction () {
      this.$emit('select', this.getSelectEventType())
      this.initializeSelectedRows()
      this.selectedRowsCount = 0
    },
    getSelectEventType () {
      if (!this.isSelectionActive) {
        return { type: 'edit' }
      } else if (!this.selectedRowsCount) {
        return { type: 'cancel' }
      } else {
        return { type: 'confirmEdit', data: this.getSelectedData() }
      }
    },
    getSelectedData () {
      const selectedData = []
      for (const [studyId, isStudyToggled] of Object.entries(this.selectedRows)) {
        if (isStudyToggled) {
          const studyDataObj = this.data.find(el => el.id === studyId)
          selectedData.push(studyDataObj)
        }
      }
      return selectedData
    },
    initializeSelectedRows () {
      this.data.forEach(el => {
        this.selectedRows[el.id] = false
      })
    },
    getFileIcon (fileName) {
      if (!fileName) {
        return 'file-alt'
      }
      const type = fileName.split('.').pop()
      switch (type) {
        case 'doc':
          return 'file-word'
        case 'docx':
          return 'file-word'
        case 'ppt':
          return 'file-powerpoint'
        case 'pptx':
          return 'file-powerpoint'
        case 'pdf':
          return 'file-pdf'
        case 'xlsx':
          return 'file-excel'
        case 'xls':
          return 'file-excel'
        default:
          return 'file-alt'
      }
    }
  },
  beforeMount () {
    this.visibleColumns = [...this.columns]
    this.initializeSelectedRows()
  }
}
</script>

<style lang="scss">
@import "../../assets/scss/variables";

.fp-row-selected {
  * {
    color: #693896 !important;
  }
}

.multivalue-highlight {
  background-color: $soft-blue;
}

tr:nth-of-type(odd) {
  .multivalue-highlight {
    background-color: #E5E6ED;
  }
}

tr:hover {
  .multivalue-highlight {
    background-color: #e9eaf1;
  }
}

.column-tag-wrapper {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.field-toggle {
  cursor: pointer;
  color: #20a8d8;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}

.search-button {
  width: 230px;
  height: 48px;
  background-color: #300296;
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: 30px;
  letter-spacing: 0;
  border-radius: 4px;
  color: white;
  text-transform: uppercase;
  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: #9256D9;
  }
}

.checkbox-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.VueTables > .row > .col-md-12 {
  display: flex;
  flex-direction: row-reverse;
}

.VueTables {
  td {
    vertical-align: middle !important;
  }
}

.filter-input {
  width: 250px !important;
  margin-right: auto;
  margin-bottom: 10px;
}

.hide-filters {
  .VueTables__search-field {
    display: none !important;
  }
  .after-filter-wrapper {
    width: 100%;
    justify-content: flex-end;
    .info-text {
      margin-right: 15px;
    }
  }
}

.search-button {
  overflow: hidden;
  white-space: nowrap;
  border-radius: 5px;
}

.el-checkbox-button.el-checkbox-button--small.is-checked > .el-checkbox-button__inner {
  background-color: #20a8d8;
  border-color: #20a8d8;
}

.VueTables__search {
  width: 100%;
  margin-bottom: 0;
}

.VueTables__search-field {
  margin-right: auto;
}

.VueTables__sort-icon {
  margin-left: 8px;
}

.VueTables__search__input {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: 0;
  border-radius: 4px !important;
  border: 2px solid $pewter !important;
  height: 40px !important;
  width: 360px !important;
}

.VueTables__limit-field {
  margin-right: 10px;
}

.VueTables__columns-dropdown-wrapper {
  margin: auto 20px auto 0;
}

.VuePagination nav {
}

.VuePagination.row.col-md-12 {
  flex: 0 !important;
}

.VuePagination {
  nav {
    justify-content: space-between;
  }
  &__count {
    width: 300px;
  }
}

.VueTables__limit-field {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: 0;

  select {
    border: 2px solid $pewter !important;
    height: 40px !important;
  }
}

.VueTables__search-field {
  label {
    display: none !important;
  }
}

.VueTables__limit {
  margin: 0;
  height: 51px;

  label {
    display: none !important;
  }

  select {
    border-radius: 4px;
  }
}

.file-data {
  width: 100%;
}

.file-data:hover {
  color: #20a8d8;
}

.file-icon {
  font-size: 1.5rem;
  float: right;
}

.after-filter-wrapper {
  padding-left: 10px;
  width: calc(100% - 360px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  .dwn-button {
    cursor: pointer;
    width: 65px;
    height: 65px;
    margin-right: 15px;
    margin-top: -10px;
  }

  .info-text {
    font-size: 12px;
    font-family: 'Source Sans Pro', sans-serif;
    color: $pewter;
  }
}
</style>
